import {
  AlertTriangle,
  ShieldOff,
  Tag,
  UserMinus,
  Building2,
  Layers,
  Trash2,
  Loader2,
} from "lucide-react";
import {
  useLeadInvisibility,
  type LeadInvisibilityReason,
  type LeadInvisibilityReasonKind,
} from "@/features/leads/api/useLeadInvisibility";

const REASON_META: Record<
  LeadInvisibilityReasonKind,
  { Icon: React.ComponentType<{ className?: string }> }
> = {
  assignment_revoked: { Icon: UserMinus },
  stage_not_in_role: { Icon: Layers },
  branch_not_in_user: { Icon: Building2 },
  custom_field_blocked: { Icon: Tag },
  soft_deleted: { Icon: Trash2 },
  not_assigned: { Icon: UserMinus },
  has_access: { Icon: ShieldOff },
};

interface Props {
  leadId: number | string | null | undefined;
}

export default function RevokedAccessExplainer({ leadId }: Props) {
  const numericId =
    leadId == null || leadId === "" ? null : Number(leadId) || null;
  const { data, isLoading } = useLeadInvisibility(numericId, {
    enabled: !!numericId,
  });

  const reasons: LeadInvisibilityReason[] = data?.reasons ?? [];

  return (
    <div className="flex-1 min-h-0 flex items-start justify-center px-6 py-10 overflow-y-auto">
      <div className="w-full max-w-[420px] flex flex-col items-center text-center gap-5">
        <div className="size-14 rounded-full bg-[#fef2f2] border border-[#fbdcdc] flex items-center justify-center">
          <ShieldOff className="size-7 text-[#da2a46]" strokeWidth={1.75} />
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-[16px] font-semibold text-[var(--color-black-10)] tracking-[-0.32px]">
            You don't have access to this lead
          </p>
          <p className="text-[13px] text-[var(--color-black-6)] tracking-[-0.26px] leading-snug">
            Your view is restricted. Below is exactly what's missing — share
            this with your admin to get access restored.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-[12px] text-[var(--color-black-6)]">
            <Loader2 className="size-3.5 animate-spin" />
            Checking reasons…
          </div>
        ) : reasons.length === 0 ? (
          <div className="w-full rounded-[12px] border border-[var(--color-black-1)] bg-white px-3 py-3 text-[12.5px] text-[var(--color-black-7)] tracking-[-0.25px]">
            No specific reason was returned. The lead may be outside your
            branch, role, or custom-field access. Contact your admin.
          </div>
        ) : (
          <ul className="w-full rounded-[12px] border border-[#fbdcdc] bg-[#fef2f2] px-3 py-3 flex flex-col gap-2 text-left">
            {reasons.map((r, i) => {
              const Icon = REASON_META[r.kind]?.Icon ?? AlertTriangle;
              return (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex items-center justify-center rounded-full bg-white p-1 border border-[#fbdcdc] shrink-0">
                    <Icon className="size-3.5 text-[#7a1c2b]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-medium text-[#5e0f1b] leading-snug tracking-[-0.25px]">
                      {r.label}
                    </p>
                    {r.detail ? (
                      <p className="text-[11.5px] text-[#7a1c2b] leading-snug mt-0.5 tracking-[-0.23px]">
                        {r.detail}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
